import React, { useState, useRef, useEffect } from 'react'
import { inject, observer } from 'mobx-react'
import { withTranslation } from 'react-i18next'
import { uploadedShapeFiles } from 'constants.js'
import PaneHeader from '../panes/PaneHeader'
import UserShapefileList from './UserShapefileList'
import ErrorForm from '../common/ErrorForm'

const UserShapefileListContainer = ({ userShapefileList, selectionStore, uiStore, t }) => {
  const initialMode = (selectionStore && selectionStore.data && selectionStore.data.initialMode) || (userShapefileList && userShapefileList.initialMode) || 'list'
  const [searchTerm, setSearchTerm] = useState('')
  const [viewMode, setViewMode] = useState(initialMode) // 'list' or 'upload'
  const [isDragging, setIsDragging] = useState(false)
  const [selectedFileObj, setSelectedFileObj] = useState(null)
  const [fileAnalysis, setFileAnalysis] = useState({ metaStr: '', hasCoords: true })
  const [showHelpFormat, setShowHelpFormat] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef(null)

  const allFiles = userShapefileList.shapefiles || []

  useEffect(() => {
    const map = uiStore && uiStore.map && uiStore.map.leafletElement
    const center = map && typeof map.getCenter === 'function' ? map.getCenter() : null
    const zoom = map && typeof map.getZoom === 'function' ? map.getZoom() : null
    if (uiStore && typeof uiStore.suppressMapBoundsTemporarily === 'function') {
      uiStore.suppressMapBoundsTemporarily(500)
    }
    const timer = setTimeout(() => {
      if (map && center && zoom != null) {
        map.setView(center, zoom, { animate: false })
      }
    }, 0)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    const mode = (selectionStore && selectionStore.data && selectionStore.data.initialMode) || (userShapefileList && userShapefileList.initialMode)
    if (mode === 'upload') {
      setViewMode('upload')
    }
    if (userShapefileList.chosenFile) {
      handleFileSelected(userShapefileList.chosenFile)
      setViewMode('upload')
    }
  }, [selectionStore && selectionStore.data && selectionStore.data.initialMode, userShapefileList && userShapefileList.initialMode, userShapefileList.chosenFile])

  const handleClose = () => {
    selectionStore.clear()
  }

  const handleFileSelected = (file) => {
    if (!file) return
    setSelectedFileObj(file)
    setViewMode('upload')

    // Format size and type
    const sizeInKB = file.size ? file.size / 1024 : 0
    const sizeStr = sizeInKB > 1024
      ? `${(sizeInKB / 1024).toFixed(1)} MB`
      : `${Math.max(1, Math.round(sizeInKB))} KB`
    const ext = ((file.name && file.name.split('.').pop()) || 'ZIP').toUpperCase()
    const metaStr = `${sizeStr} · ${ext}`

    setFileAnalysis({ metaStr, hasCoords: true })
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    const files = e.dataTransfer && e.dataTransfer.files
    if (files && files[0]) {
      handleFileSelected(files[0])
    }
  }

  const handleChooseFileClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
      fileInputRef.current.click()
    }
  }

  const handleFileInputChange = (e) => {
    const file = e.target.files && e.target.files[0]
    if (file) {
      handleFileSelected(file)
    }
  }

  const handleUploadShapefile = async () => {
    if (!selectedFileObj) return
    setIsUploading(true)
    userShapefileList.chosenFile = selectedFileObj

    if (userShapefileList.validate(selectedFileObj)) {
      try {
        await uiStore.uploadUserShapefile(userShapefileList)
        setSelectedFileObj(null)
        setViewMode('list')
      } catch (err) {
        console.error('Error uploading shapefile:', err)
      } finally {
        setIsUploading(false)
      }
    } else {
      setIsUploading(false)
    }
  }

  const handleCancelUpload = () => {
    setSelectedFileObj(null)
    userShapefileList.chosenFile = null
    userShapefileList.invalid.clear()
    setViewMode('list')
  }

  const filteredFiles = allFiles.filter(file => {
    if (!searchTerm || !searchTerm.trim()) return true
    const fileName = file.displayName || file.name || ''
    return fileName.toLowerCase().includes(searchTerm.toLowerCase().trim())
  })

  const activeFile = selectedFileObj || userShapefileList.chosenFile

  // ==========================================
  // VIEW 1 & 2: UPLOAD FLOW
  // ==========================================
  if (viewMode === 'upload') {
    return (
      <div className='shape-file-pane'>
        {/* Hidden file input */}
        <input
          type='file'
          ref={fileInputRef}
          accept='.zip,.kml,.kmz'
          style={{ display: 'none' }}
          onChange={handleFileInputChange}
        />

        {/* Pane Header */}
        <PaneHeader
          icon={uploadedShapeFiles}
          title={t('GLOBAL.UPLOAD_SHAPEFILE_TITLE', { defaultValue: 'Upload shapefile' })}
          onClose={handleClose}
        />

        <p className='shape-file-pane__subtitle'>
          {t('GLOBAL.UPLOAD_SHAPEFILE_SUBTITLE', { defaultValue: 'Upload a ZIP, KML, or KMZ file containing custom shapes.' })}
        </p>

        <div className='shape-upload'>
          {/* No file selected yet */}
          {!activeFile
            ? (
              <>
                {/* Dropzone */}
                <div
                  className={`shape-upload__dropzone ${isDragging ? 'is-dragging' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={handleChooseFileClick}
                >
                  <div className='shape-upload__dropzone-icon'>
                    <svg width='30' height='30' viewBox='0 0 24 24' fill='none' stroke='#5EB1BC' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                      <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' />
                      <polyline points='17 8 12 3 7 8' />
                      <line x1='12' y1='3' x2='12' y2='15' />
                    </svg>
                  </div>
                  <span className='shape-upload__dropzone-title'>
                    {t('GLOBAL.DROP_SHAPEFILE', { defaultValue: 'Drop a shapefile here' })}
                  </span>
                  <span className='shape-upload__dropzone-subtitle'>
                    {t('GLOBAL.OR_CHOOSE_FILE', { defaultValue: 'or choose a file from your computer' })}
                  </span>
                </div>

                {/* Supported formats info */}
                <div className='shape-upload__info-card'>
                  <div className='shape-upload__info-label'>
                    {t('GLOBAL.SUPPORTED_FORMATS', { defaultValue: 'Supported formats' })}
                  </div>
                  <div className='shape-upload__info-value'>
                    {t('GLOBAL.SUPPORTED_SHAPE_FORMATS_DESC', { defaultValue: 'ZIP, KML, or KMZ · Maximum file size 10 MB' })}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className='shape-upload__actions'>
                  <button
                    type='button'
                    className='shape-upload__btn-cancel'
                    onClick={handleCancelUpload}
                  >
                    {t('GLOBAL.CANCEL', { defaultValue: 'Cancel' })}
                  </button>
                  <button
                    type='button'
                    className='shape-upload__btn-primary'
                    onClick={handleChooseFileClick}
                  >
                    {t('GLOBAL.CHOOSE_FILE', { defaultValue: 'Choose file' })}
                  </button>
                </div>

                {/* Help link */}
                <div className='shape-upload__help-link'>
                  <button
                    type='button'
                    className='shape-upload__help-btn'
                    onClick={() => setShowHelpFormat(!showHelpFormat)}
                  >
                    {t('GLOBAL.NEED_HELP_FORMATTING', { defaultValue: 'Need help formatting your file?' })}
                  </button>
                </div>

                {showHelpFormat && (
                  <div className='shape-upload__help-modal'>
                    <span>{t('GLOBAL.ANALYZE_SHAPEFILES', { defaultValue: 'To analyze custom shapefiles, upload a Zip-compressed shapefile or a KML/KMZ file.' })}</span>
                  </div>
                )}
              </>
              )
            : (
              /* File selected */
              <>
                {/* File Card */}
                <div className='shape-upload__file-card'>
                  <div className='shape-upload__file-card-left'>
                    <div className='shape-upload__file-icon-badge'>
                      <svg
                        width='20'
                        height='20'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='#5EB1BC'
                        strokeWidth='1.75'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      >
                        <polygon points='12 2 2 7 12 12 22 7 12 2' />
                        <polyline points='2 17 12 22 22 17' />
                        <polyline points='2 12 12 17 22 12' />
                      </svg>
                    </div>
                    <div className='shape-upload__file-card-info'>
                      <span className='shape-upload__file-card-name' title={(activeFile && (activeFile.displayName || activeFile.name)) || 'custom-boundaries.zip'}>
                        {(activeFile && (activeFile.displayName || activeFile.name)) || 'custom-boundaries.zip'}
                      </span>
                      <span className='shape-upload__file-card-meta'>
                        {fileAnalysis.metaStr || (activeFile && activeFile.size ? `${Math.max(1, Math.round(activeFile.size / 1024))} KB · ${(activeFile.name ? activeFile.name.split('.').pop() : 'ZIP').toUpperCase()}` : '2.4 MB · ZIP')}
                      </span>
                    </div>
                  </div>
                  <button
                    type='button'
                    className='shape-upload__file-card-change'
                    onClick={handleChooseFileClick}
                  >
                    {t('GLOBAL.CHANGE', { defaultValue: 'Change' })}
                  </button>
                </div>

                {/* Detection Status Banner */}
                <div className={`shape-upload__status-banner ${!fileAnalysis.hasCoords ? 'has-error' : ''}`}>
                  <div className='shape-upload__status-top'>
                    <div className='shape-upload__status-icon'>
                      <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='#0D9488' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
                        <circle cx='12' cy='12' r='10' />
                        <polyline points='9 12 11 14 15 10' />
                      </svg>
                    </div>
                    <span className='shape-upload__status-text'>
                      {t('GLOBAL.SHAPE_COORDS_DETECTED', { defaultValue: 'Shape geometries and coordinates detected' })}
                    </span>
                  </div>
                  <div className='shape-upload__status-bar' />
                </div>

                {/* Before upload info card */}
                <div className='shape-upload__before-card'>
                  <span className='shape-upload__before-title'>
                    {t('GLOBAL.BEFORE_UPLOAD', { defaultValue: 'Before upload' })}
                  </span>
                  <span className='shape-upload__before-item'>
                    {t('GLOBAL.VALID_GEOMETRY_READY', { defaultValue: 'Valid geometry found · Ready to upload' })}
                  </span>
                  <span className='shape-upload__before-subitem'>
                    {t('GLOBAL.INVALID_BOUNDARIES_SKIPPED', { defaultValue: 'Invalid boundaries will be skipped.' })}
                  </span>
                </div>

                {/* Upload error if any */}
                {userShapefileList.error && (
                  <div className='shape-file-pane__error'>
                    <ErrorForm item={userShapefileList} />
                  </div>
                )}
                {userShapefileList.invalid.get('file') && (
                  <div className='shape-file-pane__error'>
                    {userShapefileList.invalid.get('file')}
                  </div>
                )}

                {/* Action Buttons */}
                <div className='shape-upload__actions'>
                  <button
                    type='button'
                    className='shape-upload__btn-cancel'
                    onClick={handleCancelUpload}
                    disabled={isUploading}
                  >
                    {t('GLOBAL.CANCEL', { defaultValue: 'Cancel' })}
                  </button>
                  <button
                    type='button'
                    className='shape-upload__btn-primary'
                    onClick={handleUploadShapefile}
                    disabled={isUploading}
                  >
                    {isUploading
                      ? (
                        <>
                          <div className='shape-file-pane__spinner' style={{ width: 16, height: 16, borderWidth: 2 }} />
                          <span>{t('GLOBAL.UPLOADING', { defaultValue: 'Uploading...' })}</span>
                        </>
                        )
                      : (
                        <span>{t('GLOBAL.UPLOAD_SHAPES', { defaultValue: 'Upload shapefile' })}</span>
                        )}
                  </button>
                </div>
              </>
              )}
        </div>
      </div>
    )
  }

  // ==========================================
  // VIEW 3: LIST / MANAGEMENT FLOW
  // ==========================================
  return (
    <div className='shape-file-pane'>
      {/* Pane Header */}
      <PaneHeader
        icon={uploadedShapeFiles}
        title={t('GLOBAL.MY_SHAPEFILES_TITLE', { defaultValue: 'My Shapefiles' })}
        onClose={handleClose}
      />

      <p className='shape-file-pane__subtitle'>
        {t('GLOBAL.MY_SHAPEFILES_SUBTITLE', { defaultValue: 'Search or manage your custom shapefiles' })}
      </p>

      {/* Search Input */}
      <div className='shape-file-pane__search-wrap'>
        <span className='shape-file-pane__search-icon'>
          <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
            <circle cx='11' cy='11' r='8' />
            <line x1='21' y1='21' x2='16.65' y2='16.65' />
          </svg>
        </span>
        <input
          type='text'
          className='shape-file-pane__search-input'
          placeholder={t('GLOBAL.SEARCH_SHAPEFILES', { defaultValue: 'Search shapefiles...' })}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <button
            type='button'
            className='shape-file-pane__search-clear'
            onClick={() => setSearchTerm('')}
            aria-label='Clear search'
            style={{ width: '24px', height: '24px', minWidth: '24px', maxWidth: '24px', padding: 0 }}
          >
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
              <line x1='18' y1='6' x2='6' y2='18' />
              <line x1='6' y1='6' x2='18' y2='18' />
            </svg>
          </button>
        )}
      </div>

      {/* Uploaded Files Section Header */}
      <div className='shape-file-pane__section-header'>
        <span className='shape-file-pane__section-title'>
          {t('GLOBAL.UPLOADED_SHAPEFILES', { defaultValue: 'Uploaded shapefiles' })}
        </span>
        <span className='shape-file-pane__files-count'>
          {filteredFiles.length} {filteredFiles.length === 1 ? 'file' : 'files'}
        </span>
      </div>

      {/* Shapefile List */}
      <div className='shape-file-pane__list-container'>
        {userShapefileList.loading
          ? (
            <div className='shape-file-pane__loading'>
              <div className='shape-file-pane__spinner' />
              <span>{t('GLOBAL.LOADING', { defaultValue: 'Loading shapefiles...' })}</span>
            </div>
            )
          : (
            <UserShapefileList
              userShapefileList={userShapefileList}
              shapefiles={filteredFiles}
              onSelect={async (shapefile) => {
                uiStore.processing = true
                await shapefile.loadLevels()
                userShapefileList.selectedFile = shapefile
                uiStore.processing = false
              }}
            />
            )}
      </div>

      {/* Footer / Upload New Button */}
      <div className='shape-file-pane__footer'>
        <button
          type='button'
          className='shape-file-pane__upload-btn'
          disabled={userShapefileList.hasExceededShapefilesLimit}
          onClick={() => {
            setSelectedFileObj(null)
            userShapefileList.chosenFile = null
            setViewMode('upload')
          }}
        >
          <span className='shape-file-pane__upload-icon'>
            <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
              <line x1='12' y1='5' x2='12' y2='19' />
              <line x1='5' y1='12' x2='19' y2='12' />
            </svg>
          </span>
          <span>{t('GLOBAL.UPLOAD_SHAPEFILE', { defaultValue: 'Upload shapefile' })}</span>
        </button>
      </div>
    </div>
  )
}

export default withTranslation()(inject('uiStore', 'selectionStore')(observer(UserShapefileListContainer)))
